import { Collapse, FormControlLabel, IconButton } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import lodash, { isNil } from "lodash";

import { getTranslateByKey, isNullUndefined, isShowControlByElementId } from "@/main/services/utils";
import { getCheckoutSuccessMessage, isCheckoutAwaitingApproval } from "@/main/services/utilsCheckout";

import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import ProcurementStatusComponent from "@/main/components/shared/status/procurementStatusComponent";

import * as S from "./styles";
import * as T from "./types";
import CollapseQuoteStep3 from "../collapseQuoteStep3/collapseQuoteStep3";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import { quoteApi } from "@/main/api";
import Button from "@/main/components/shared/button/button";
import { Controls } from "@/main/services/constants/constantsControl";
import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import { OrderStatusEnum } from "@/main/services/enum/OrderStatusEnum";
import { AwaitingReleaseResonEnum } from "@/main/services/enum/AwaitingReleaseResonEnum";
import { QuoteManualChekoutResponse } from "@/main/models/response";
import { toast } from "react-toastify";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import { resetReducerByIdAction, useAutomaticActionReducer } from "@/main/store/automaticAction";
import { AutomaticActionEnum } from "@/main/services/enum/AutomaticActionEnum";
import { getRefreshForceUrl } from "@/main/services/utilsQueryParams";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RoutesConstants } from "@/main/routes/routesConstants";

const INITIAL_CHECKOUT_RESULT: T.CheckoutResult = {
	success: false,
	procurementGenerated: false,
	wentToAwaitingApproval: false,
};

const ContentCard = ({
	quote,
	hasWaitinReleasePermission,
	optionsSelected,
	refreshClaim,
	refreshClaimCounts,
	handleToggleCheckbox,
}: T.IContentCardProps) => {
	const [expanded, setExpanded] = useState(false);

	function renderText(text: string | undefined, hasTooltip: boolean = false) {
		if (hasTooltip) return <S.TooltipText title={text as string}>{text}</S.TooltipText>;

		return text;
	}

	// Handle Functions
	function handleClickRow(e: React.MouseEvent<any>) {
		e.stopPropagation();
		setExpanded(!expanded);
	}

	// Constants Layout

	return (
		<S.boxContainer>
			<S.ContentContainer>
				<S.Content id={`id-searchClaim-row-quote-${quote.guidQuote}`} onClick={handleClickRow}>
					<h5>
						<b>{renderText(`${getTranslateByKey("information.quote")} - ${quote.code}`, true)}</b>
					</h5>
					<ColumnLabel
						label=""
						content={
							<ProcurementStatusComponent
								status={{
									id: Number(quote.idProcurementStatus),
									name: quote.nameProcurementStatus,
								}}
							/>
						}
						propsLabel={{ style: { textAlign: "center" } }}
					/>
					<S.AreaIcon>
						<IconButton onClick={handleClickRow} id={`id-searchOrder-button-colapse`}>
							{expanded === false ? (
								<ExpandMore aria-expanded={expanded} aria-label={getTranslateByKey("information.showMore")} />
							) : (
								<ExpandLess aria-expanded={expanded} aria-label={getTranslateByKey("information.showMore")} />
							)}
						</IconButton>
					</S.AreaIcon>
					{quote.hasAnswersForAllItems &&
						(quote.idProcurementStatus != OrderStatusEnum.WaitingRelease || hasWaitinReleasePermission) && (
							<KogCheckbox
								id={`id-createOrder-checkbox-select-${quote.guidQuote}`}
								checked={optionsSelected.includes(quote.guidQuote)}
								value={optionsSelected.includes(quote.guidQuote)}
								onChange={() => handleToggleCheckbox(quote.guidQuote)}
								onClick={(e) => e.stopPropagation()}
							/>
						)}
				</S.Content>
			</S.ContentContainer>
			<Collapse in={expanded} timeout="auto" mountOnEnter>
				<S.ContentCollapse>
					<CollapseQuoteStep3
						guidQuote={quote.guidQuote}
						refreshClaimCounts={refreshClaimCounts}
						refreshClaim={refreshClaim}
					/>
				</S.ContentCollapse>
			</Collapse>
		</S.boxContainer>
	);
};

export default function CardGridQuote({
	isOpenModal,
	guidClaim,
	claimNumber,
	guidBillingAddress,
	refreshClaim,
	refreshClaimCounts,
	closeModal,
	infiniteScrollConfig,
	setIsLoading,
}: T.ICardGridProps) {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const hasWaitinReleasePermission = isShowControlByElementId(Controls.IdReleaseOrderControl);

	const { automaticAction } = useAutomaticActionReducer();

	const INITIAL_GRID_DATA: T.IGridData = {
		data: [],
		totalItems: 0,
	};

	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
	const [gridData, setGridData] = useState<T.IGridData>(INITIAL_GRID_DATA);
	const [gridLoading, setGridLoading] = useState<boolean>(true);
	const [generateOrderLoading, setGenerateOrderLoading] = useState<boolean>(false);
	const [disabledGenerateOrderButton] = useState<boolean>(false);
	const [optionsSelected, setOptionsSelected] = useState<string[]>([]);
	const [hasClaimchanges, setHasClaimchanges] = useState<boolean>(false);

	const allowedQuotes = gridData.data.filter(
		(quote) =>
			quote.hasAnswersForAllItems &&
			(quote.idProcurementStatus != OrderStatusEnum.WaitingRelease || hasWaitinReleasePermission),
	);
	const allCheckboxesSelected = useMemo(() => {
		if (isNullUndefined(gridData)) return false;

		return allowedQuotes.every((item) => optionsSelected.includes(item.guidQuote));
	}, [JSON.stringify(optionsSelected), gridData]);

	const isAutoGeneratingRef = useRef(false);

	// Notation: Stay on Search Claims re-searching by Claim Nr so parts in any status (incl. pending approval / waiting release) remain visible
	function navigateRefreshSearchClaimByClaimNumber() {
		navigate(`${RoutesConstants.claimSearchClaims}${getRefreshForceUrl({ claimNumber })}`);
	}

	useEffect(() => {
		const findAction = automaticAction.data?.find(
			(x) => x.idAction == AutomaticActionEnum.GenerateOrderBySaveBuyerPartAsnwerByClaim && x.id == guidClaim,
		);

		if (isNil(findAction)) {
			isAutoGeneratingRef.current = false;
			return;
		}

		if (generateOrderLoading) return;

		if (!isAutoGeneratingRef.current) {
			isAutoGeneratingRef.current = true;
			setOptionsSelected([]);
			if (!gridLoading) loadQuotes();
			return;
		}

		if (gridLoading) return;

		if (allowedQuotes.length === 0) {
			isAutoGeneratingRef.current = false;
			dispatch(resetReducerByIdAction(guidClaim));
			navigateRefreshSearchClaimByClaimNumber();
			return;
		}

		if (!allowedQuotes.every((quote) => optionsSelected.includes(quote.guidQuote))) {
			handleToggleCheckboxAll();
			return;
		}

		isAutoGeneratingRef.current = false;
		dispatch(resetReducerByIdAction(guidClaim));
		handleGenerateOrders(true);
	}, [automaticAction.data, gridData, gridLoading, optionsSelected, generateOrderLoading]);

	function loadQuotes() {
		setGridLoading(true);
		quoteApi
			.getQuoteManualByGuidClaim(guidClaim)
			.then((response) => {
				setGridData({
					data: response.data.result,
					totalItems: response.data.result.length,
				});
			})
			.finally(() => {
				setGridLoading(false);
			});
	}

	function loadQuotesAndSetHasChanges() {
		setHasClaimchanges(true);
		loadQuotes();
	}

	useEffect(() => {
		loadQuotes();
	}, []);

	useEffect(() => {
		//on unmount component
		return () => {
			if (hasClaimchanges) {
				refreshClaim();
				refreshClaimCounts();
			}
		};
	}, [hasClaimchanges]);

	function handleToggleCheckbox(guidQuote: string | string[]) {
		const cloneOptionsSelected: string[] = lodash.cloneDeep(optionsSelected);

		const isGuidArray = Array.isArray(guidQuote);

		if (!isGuidArray) {
			const index = cloneOptionsSelected.indexOf(guidQuote);

			if (index > -1) {
				cloneOptionsSelected.splice(index, 1);
			} else {
				cloneOptionsSelected.push(guidQuote);
			}
		} else {
			guidQuote.forEach((guid) => {
				const index = cloneOptionsSelected.indexOf(guid);
				if (index > -1) {
					cloneOptionsSelected.splice(index, 1);
				} else {
					cloneOptionsSelected.push(guid);
				}
			});
		}

		setOptionsSelected(cloneOptionsSelected);
	}

	function handleToggleCheckboxAll() {
		if (isNullUndefined(gridData)) return;

		const allCheckboxesSelected = allowedQuotes.every((item) => optionsSelected.includes(item.guidQuote));

		if (allCheckboxesSelected) {
			const allGuids = allowedQuotes.map((item) => item.guidQuote);

			handleToggleCheckbox(allGuids);
		} else {
			const allIdsUnselected = allowedQuotes.reduce((acc, item) => {
				if (!optionsSelected.includes(item.guidQuote)) {
					acc.push(item.guidQuote);
				}
				return acc;
			}, [] as string[]);
			handleToggleCheckbox(allIdsUnselected);
		}
	}

	async function searchSupplierForParts(quote: QuoteManualChekoutResponse): Promise<boolean> {
		const res = await quoteApi.addMissingItemsToCart(String(quote.guidQuote));
		const findSupplier = res.data.result;
		if (!findSupplier) {
			toast.error(
				`${getTranslateByKey("information.quote")} ${quote.code}: ${getTranslateByKey("information.noSuppliersWereFoundForTheParts")}`,
			);
			return false;
		}
		return true;
	}

	async function changeQuoteToAwaitingRelease(quote: QuoteManualChekoutResponse): Promise<boolean> {
		const res = await quoteApi.changeQuoteToAwaitingRelease(quote.guidQuote);
		if (res.status < 400 && res.data.result) {
			toast.success(
				`${getTranslateByKey("information.quote")} ${quote.code}: ${getTranslateByKey("information.quoteChangedToAwaitingRelease")}`,
			);
			return true;
		}
		return false;
	}

	async function submitGenerateOrder(quote: QuoteManualChekoutResponse): Promise<T.CheckoutResult> {
		const requestCheckout = {
			guidQuote: quote.guidQuote,
			guidBillingAddress: guidBillingAddress,
		};

		const res = await quoteApi.checkout(requestCheckout);
		if (res.status < 400 && res.data.result) {
			toast.success(
				`${getTranslateByKey("information.quote")} ${quote.code}: ${getCheckoutSuccessMessage(res.data.result)}`,
			);
			return {
				success: true,
				procurementGenerated: true,
				wentToAwaitingApproval: isCheckoutAwaitingApproval(res.data.result),
			};
		}
		return { ...INITIAL_CHECKOUT_RESULT };
	}

	async function generateOrder(quote: QuoteManualChekoutResponse): Promise<T.CheckoutResult> {
		const result: T.CheckoutResult = { ...INITIAL_CHECKOUT_RESULT };

		if (!quote.hasAllItems && quote.hasAnswersForAllItems) {
			const messageModal = getTranslateByKey("information.areYouThatSystemSelectBestSupplierAndCheckOutYourQuote");
			const resultConfirmation = await getConfirmation(
				`${getTranslateByKey("information.quote")} ${quote.code}: ${messageModal}`,
			);

			if (!resultConfirmation) return result;

			result.success = await searchSupplierForParts(quote);
			result.procurementGenerated = result.success;
			if (!result.success) return result;
		}

		if (quote?.idProcurementStatus != OrderStatusEnum.WaitingRelease || hasWaitinReleasePermission) {
			const response = await quoteApi.validateawaitingreleaseaprroval(quote.guidQuote);
			const resData = response.data.result;
			const validateTotalLoss = {
				approved: resData.approved,
				awaitingReleaseReson: resData.awaitingReleaseReson,
				changedToAwaitingRelease: resData.changedToAwaitingRelease,
			};

			if (!validateTotalLoss.changedToAwaitingRelease) {
				if (
					validateTotalLoss &&
					!validateTotalLoss.approved &&
					!isNullUndefined(validateTotalLoss.awaitingReleaseReson)
				) {
					const MESSAGES_MODAL_AWAITING_RELEASE = {
						[AwaitingReleaseResonEnum.QuoteChangeStatusDamagePayment]: getTranslateByKey(
							"information.thisQuoteIsADamagePaymentAreYouSureYouWantToGenerateTheOrder",
						),
						[AwaitingReleaseResonEnum.QuoteChangeStatusTotalLoss]: getTranslateByKey(
							"information.thisQuoteIsATotalLossAreYouSureYouWantToGenerateTheOrder",
						),
						[AwaitingReleaseResonEnum.QuoteChangeStatusNotDeductible]: getTranslateByKey(
							"information.thisQuoteHasLessThanDeductibleRepairCostsAreYouSureYouWantToGenerateTheOrder",
						),
						[AwaitingReleaseResonEnum.QuoteChangeStatusPossibleTotalLoss]: getTranslateByKey(
							"information.thatQuoteMightBeATotalLossAreYouSureThatWantToGenerateTheOrder",
						),
						[AwaitingReleaseResonEnum.QuoteChangeStatusNotAprroved]: getTranslateByKey(
							"information.thisQuoteIsNotApprovedAreYouSureYouWantToGenerateTheOrder",
						),
					};

					if (isNullUndefined(validateTotalLoss.awaitingReleaseReson)) return result;

					const messageReason =
						MESSAGES_MODAL_AWAITING_RELEASE[
							validateTotalLoss.awaitingReleaseReson as keyof typeof MESSAGES_MODAL_AWAITING_RELEASE
						] ?? "";
					const messageModal = `${getTranslateByKey("information.quote")} ${quote.code}: ${messageReason}`;

					let resultModal = hasWaitinReleasePermission;

					if (resultModal) resultModal = await getConfirmation(messageModal);

					if (!resultModal) {
						result.success = await changeQuoteToAwaitingRelease(quote);
						result.procurementGenerated = result.success;
						return result;
					}
				}
				return await submitGenerateOrder(quote);
			} else {
				toast.success(
					`${getTranslateByKey("information.quote")} ${quote.code}: ${getTranslateByKey("information.quoteChangedToAwaitingRelease")}`,
				);
				result.success = true;
				return result;
			}
		} else {
			toast.error(
				`${getTranslateByKey("information.quote")} ${quote.code}: ${getTranslateByKey("information.awaitingReleaseWithoutPermission")}`,
			);
			return result;
		}
	}

	async function handleGenerateOrders(isAutomaticGeneration: boolean = false) {
		let hasChanges = false;
		setGenerateOrderLoading(true);
		setIsLoading(true);
		for (const quoteGuid of optionsSelected) {
			try {
				const quote = gridData.data.find((q) => q.guidQuote == quoteGuid);
				if (!quote) continue;
				const result = await generateOrder(quote);
				hasChanges ||= result.success;
			} catch {}
		}
		setGenerateOrderLoading(false);
		setIsLoading(false);

		closeModal();

		if (hasChanges || isAutomaticGeneration) {
			navigateRefreshSearchClaimByClaimNumber();
		}
	}

	function renderGrid() {
		if (gridLoading) return <LoadingComponent />;
		if (gridData.data.length === 0) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
		if (gridData.data.length === 0 && !gridLoading) return null;
		return (
			<S.ContainerCard>
				{gridData.data.map((quote) => (
					<ContentCard
						key={quote.guidQuote}
						quote={quote}
						hasWaitinReleasePermission={hasWaitinReleasePermission}
						optionsSelected={optionsSelected}
						refreshClaim={loadQuotesAndSetHasChanges}
						refreshClaimCounts={refreshClaimCounts}
						handleToggleCheckbox={handleToggleCheckbox}
					/>
				))}
			</S.ContainerCard>
		);
	}

	return (
		<>
			<KogModalSideComponent
				open={isOpenModal}
				shouldRenderOpen
				title={getTranslateByKey("information.generateOrders")}
				closeModal={closeModal}
				width="85%"
			>
				{allowedQuotes.length > 0 && (
					<S.AreaButton>
						<FormControlLabel
							label={getTranslateByKey("information.selectAll")}
							labelPlacement="start"
							control={
								<KogCheckbox
									id={"id-createOrder-checkbox-selectAll"}
									checked={allCheckboxesSelected}
									value={allCheckboxesSelected}
									onChange={() => handleToggleCheckboxAll()}
								/>
							}
						/>
					</S.AreaButton>
				)}
				<S.Container id="id-general-cardGridOrder">
					{infiniteScrollConfig ? (
						<div style={{ minHeight: "95vh" }}>
							<InfiniteScroll
								style={{ overflowX: "hidden" }}
								dataLength={infiniteScrollConfig.dataLength}
								next={infiniteScrollConfig.fetchData}
								hasMore={infiniteScrollConfig.hasMore}
								loader={infiniteScrollConfig.isLoading ? <LoadingComponent /> : <></>}
								endMessage={
									<S.EndMessage className="d-flex d-flex justify-content-center">
										{getTranslateByKey("information.allResultsWereDisplayed")}
									</S.EndMessage>
								}
							>
								{renderGrid()}
							</InfiniteScroll>
						</div>
					) : (
						renderGrid()
					)}
				</S.Container>
				<S.AreaButton>
					<Button
						id={"claims-button-generateOrder"}
						type="button"
						isLoadingLockScreen
						isLoading={generateOrderLoading}
						disabled={disabledGenerateOrderButton || optionsSelected.length == 0}
						onClick={() => handleGenerateOrders()}
					>
						{getTranslateByKey("information.generateOrder")}
					</Button>
				</S.AreaButton>
			</KogModalSideComponent>

			<ModalConfirmation />
		</>
	);
}
